import type { FormInstance } from "antd";
import { useEffect } from "react";
import dayjs from "dayjs";
import { useUserFormFieldsQuery } from "@/hooks/services";
import type { PurchaseContractFormValues } from "../type";
import { useModal } from "@/hooks/modals/useModal";
import { getDeptAndManagerByApplicant } from "@/apis/dept.api";
import { ContractListItemDetailDTO } from "@/types/apis";
import { mapFilesByType } from "@/utils/file";

export function useInitFormValues(
  form: FormInstance<PurchaseContractFormValues>,
  detail?: ContractListItemDetailDTO,
) {
  const userInfo = useUserFormFieldsQuery();
  const { open } = useModal("PURCHASE_CONTRACT");

  useEffect(() => {
    if (!open) return;

    if (detail) {
      initFormValuesFromDetail(form, detail);
      return;
    }

    // 根据申请人获取部门及部门主管
    const initDept = async (value: string | number) => {
      const res = await getDeptAndManagerByApplicant(value);
      if (res.data) {
        form.setFieldsValue({
          departmentName: res.data.data.deptName,
          departmentManagerName: res.data.data.managerNickName,
        });
      }
    };

    if (userInfo) {
      form.setFieldsValue({
        username: userInfo.nickname,
        employeeCode: userInfo.username,
        createDate: dayjs().format("YYYY-MM-DD"),
        department: userInfo.deptName,
        postName: userInfo.postName,
        email: userInfo.email,

        applicantId: userInfo.userId,
        applicantName: userInfo.username,
        applicantNickname: userInfo.nickname,
      });

      initDept(userInfo.userId);
    }

    form.setFieldsValue({
      currency: "CNY",
    });
  }, [form, userInfo, open, detail]);
}

function initFormValuesFromDetail(
  form: FormInstance<PurchaseContractFormValues>,
  detail: ContractListItemDetailDTO,
) {
  form.setFieldsValue({
    username: detail.purchaserUserName,
    employeeCode: detail.purchaserUserUsername,
    createDate: dayjs(detail.createTime).format("YYYY-MM-DD"),
    department: detail.deptName,
    postName: detail.postName,
    email: detail.purchaserUserEmail,

    supplierId: detail.supplierUserId,
    supplierCode: detail.supplierUserCode,
    supplierNameZh: detail.supplierUserName,
    supplierNameEn: detail.supplierUsUserName,

    contractName: detail.contractName,
    contractNumber: detail.contractNumber,

    currency: detail.currency,
    rate: detail.rate,
    inputAmount: detail.inputAmount,
    amountHkd: detail.amountHkd,
    contractValidity: dayjs(detail.contractValidity),
    contractContent: detail.contractContent,

    applicantId: detail.userId,
    applicantName: detail.userName,
    applicantNickname: detail.userNickName,
    departmentName: detail.userDept,
    departmentManagerName: detail.userDeptManager,
    factory: detail.factory,
    contractSource: detail.contractSource,

    approvedPurchaseApplyNo: detail.purchaseRequestNumber,
    approvedPurchaseApplyId: detail.purchaseRequestId,

    materialType: detail.materialTypeValue,
    contractCategory: detail.contractCategoryValue,
    contractSubCategory: detail.contractSubcategoryValue,
    contractDescription: detail.contractDescriptionValue,

    contractReviewItems: detail.contractReviewItems
      ? JSON.parse(detail.contractReviewItems)
      : {},

    // 相关文件
    relatedFiles: mapFilesByType(
      detail.srmSupplierFlowBusinessFiles,
      "relatedFiles",
    ),

    // 附件
    attachments: mapFilesByType(
      detail.srmSupplierFlowBusinessFiles,
      "attachments",
    ),

    // 合同文件
    contractFiles: mapFilesByType(
      detail.srmSupplierFlowBusinessFiles,
      "contractFiles",
    ),

    // 已签署的合同文件
    signedContracts: mapFilesByType(
      detail.srmSupplierFlowBusinessFiles,
      "signedContracts",
    ),

    // 最终合同相关附件
    finalContractRelatedAttachments: mapFilesByType(
      detail.srmSupplierFlowBusinessFiles,
      "finalContractRelatedAttachments",
    ),
    kmsContractNumber: detail.kmsContractNumber,
  });
}
